import React, { useState, useEffect } from "react";

import { Col, Row } from "react-bootstrap";
import Axios from "axios";
import DatePicker from "react-datepicker";
import JSZip from "jszip";
import MaterialTable from "material-table";
import moment from "moment-timezone";

import { useFormatter } from "../../../util/formatter";
import { useSolarCalc } from "../../../util/calculations";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import usePeakLabels from "../../../util/peakLabels";
import "../../../styles/general.css";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";

function SummaryReport(props) {
    window.JSZip = JSZip;
    const user = useUser();
    const [siteSet, setSiteSet] = useState("");
    const [country, setCountry] = useState("");
    const [isReady, setIsReady] = useState(false);
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [formatNumber] = useFormatter();
    const [selectedMonth, setSelectedMonth] = useState(
        moment().subtract(1, "month")
    );
    const [yearMonth, setYearMonth] = useState(selectedMonth.format("MMMM YYYY"));
    const [selectedYear, setSelectedYear] = useState(
        selectedMonth.format("YYYY")
    );
    const minDate = moment("2021-01-01", "YYYY-MM-DD").toDate();
    const maxDate = moment().subtract(1, "month").endOf("month").toDate();

    const [isDisabled, setIsDisabled] = useState(null);
    const solarCalc = useSolarCalc();
    const peakLabels = usePeakLabels(country);

    /**
     * Format summary sites array
     * @param {Summary Sites} summarySites
     * @returns array - formatted summary sites
     */
    const buildTableOpts = (summarySites) => {
        const show_monthly_unitprice = moment(selectedMonth, "MMMM YYYY", true).diff(moment("2024-09-01", "YYYY-MM-DD")) >= 0;
        const columns = [
            { title: "Entity", field: "entity_id", width: 70 },
            { title: "Client", field: "company", width: 250 },
            { title: "Site Name", field: "site_name", width: 250 },
            { title: "MSA Date", field: "msa_date", width: 150 },
            { title: "Site Code", field: "site_code", width: 100 },
            { title: "Invoice Number", field: "invoice_no", width: 200 },
            { title: "Invoice Date", field: "invoice_date", width: 150 },
            { title: "Invoice Due Date", field: "invoice_due_date", width: 150 },
            { title: "Unit Cost", field: "unit_cost", width: 100 },
            show_monthly_unitprice ? { title: "Quantity", field: "msa_day", width: 70 } : { title: "No. of Days", field: "msa_day", width: 70 },
            { title: "Amount Ex VAT", field: "amount_ex_VAT", width: 150 },
            { title: "VAT", field: "vat", width: 150 },
            { title: "Total Inc VAT", field: "amount_inc_VAT", width: 150 },
            { title: "Note", field: "note", width: 250 }
        ];

        const data = summarySites.map((site) => calculateFunction(site));

        setColumns(columns);
        setRows(data);
        setIsReady(true);
    };

    /**
     * Fetch data for summary report of site groups(MK1, MK2, CP)
     * @param {Site group to fetch} siteSet
     */
    const fetchData = (siteSet) => {
        let clientID = "";

        const fromDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
        const endOfMonth = selectedMonth.endOf("month");
        const toDateParam = endOfMonth.format("YYYYMMDD");
        const YYYYMM = moment(fromDate).format("YYYYMM");

        let column = "admin";
        let id = "all";
        let country = "TH";

        // Client selected
        if (clientID != "") {
            column = "client_id";
            id = clientID;
        }

        // monthly MSA sites Request
        const msaURI = `/site/msa/moment/${toDateParam}/filter/${column}/${id}`;

        // Make Requests
        GYAxios.get(msaURI).then(res => {
            let summarySites = [];
            const invoice_date = selectedMonth.endOf("month").format("YYYY-MM-DD");
            res.data.forEach((site, id) => {
                let _invoice_date = site.inv_issued_date == null ? invoice_date : moment(site.inv_issued_date).format("YYYY-MM-DD");
                summarySites.push({ ...site, invoice_date: _invoice_date });
            })
            buildTableOpts(summarySites);
        }).catch(err => console.log("Request error: ", err))
    };

    /**
     * Calculate data of TH sites
     * @param {obj} site
     * @returns TH sites data
     */
    const calculateFunction = (site) => {
        const show_monthly_unitprice = moment(selectedMonth, "MMMM YYYY", true).diff(moment("2024-09-01", "YYYY-MM-DD")) >= 0;

        const additional_info = site.additional_info ? JSON.parse(site.additional_info) : null;
        const msa_day = site.msa_day;
        const unit_price = show_monthly_unitprice ? site?.unit_price : additional_info?.msa_price?.unit_price;
        const discount = site.special_discount;
        const amount_ex_VAT = unit_price * msa_day + discount;
        const vat = site.vat * amount_ex_VAT;
        const amount_inc_VAT = amount_ex_VAT + vat;

        const values = {
            entity_id: "TH",
            invoice_no: site.invoice_no !== null ? site.invoice_no : "-",
            invoice_date: site.invoice_date,
            invoice_due_date: moment(site.invoice_date, "YYYYMMDD").add(parseFloat(site.credit_term), "day").format("YYYY-MM-DD"),
            company: site.company,
            site_code: site.site_code,
            site_name: site.site_name,
            msa_day: site.msa_day > 0 ? site.msa_day : "-",
            msa_date: site.msa ? moment(site.msa).format("YYYY-MM-DD") : "-",
            note: site.msa_description ? site.msa_description : "-",
            unit_cost: formatNumber(unit_price?.toFixed(2), 2),
            amount_ex_VAT: amount_ex_VAT ? formatNumber(amount_ex_VAT?.toFixed(2), 2) : "-",
            amount_inc_VAT: amount_inc_VAT ? formatNumber(amount_inc_VAT?.toFixed(2), 2) : "-",
            vat: amount_ex_VAT ? formatNumber(vat.toFixed(2), 2) : "-",
        };
        return values;
    };

    /**
     * Show under-review notifications
     */
    const alertMsg = () => {
        Noti.create({
            type: "w",
            msg: "Invoices of the group are still under review",
        });
    };

    useEffect(() => {
        const KCToken = user.sub;
        GYAxios.get(`/users/filter?column=keycloak_id&value=${KCToken}`).then(
            (res) => {
                const userInfo =
                    res.data.error == null && res.data.length > 0 ? res.data[0] : false;

                if (userInfo) {
                    setSiteSet(userInfo.country);
                    setCountry(userInfo.country);
                }
            }
        );
    }, []);

    useEffect(() => {
        setIsReady(false);
        fetchData(siteSet);

        const newYearMonth = selectedMonth.format("MMMM YYYY");
        const newYear = selectedMonth.format("YYYY");

        if (newYearMonth !== yearMonth) setYearMonth(yearMonth);
        if (newYear !== selectedYear) setSelectedYear(newYear);
    }, [siteSet, peakLabels, selectedMonth]);

    return (
        <>
            <div className="perf-table panel panel-default">
                <div className="panel-heading ">
                    <Row>
                        <Col md="12" style={{ marginBottom: "10px" }}>
                            <h2 className="panel-title" id="date">
                                Summary table - MSA Report
                            </h2>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <div className="month-selector">
                                Choose another Month: &nbsp;&nbsp;
                                <DatePicker
                                    minDate={localTimeToPickerTime(minDate)}
                                    maxDate={localTimeToPickerTime(maxDate)}
                                    selected={localTimeToPickerTime(selectedMonth.toDate())}
                                    onChange={(date) => {
                                        setSelectedMonth(moment(pickerTimeTolocalTime(date)));
                                    }}
                                    dateFormat="MMM yyyy"
                                    showMonthYearPicker
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="panel-body">
                    <MaterialTable
                        columns={columns}
                        data={rows}
                        isLoading={!isReady}
                        options={{
                            showTitle: false,
                            exportButton: true,
                            exportAllData: true,
                            grouping: true,
                            emptyRowsWhenPaging: false,
                            pageSize: 10,
                            pageSizeOptions: [10, 20, 40, 60],
                            headerStyle: {
                                fontSize: "1rem",
                                textAlign: "center",
                            },
                            cellStyle: {
                                textAlign: "center",
                            },
                            fixedColumns: {
                                left: 3,
                            },
                            exportFileName: `SummRepMSA-${yearMonth}`,
                        }}
                    />
                </div>
            </div>
            <Noti />
        </>
    );
}

export default SummaryReport;
